
import React, { useState, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { handleGoogleLogin, handleAppleLogin, registerUser, loginUser, updateUserProfile, updateUserSettings, sendPasswordResetEmail } from '../services/firebaseService';
import { X, Mail, Lock, User, LogIn, UserPlus, AlertCircle, Loader2, Copy, Check, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/** Detect in-app browsers (WebViews) that Google/Apple block for OAuth.
 *  Skip detection on native Capacitor apps – the app itself runs in a WebView
 *  but uses Chrome Custom Tabs / ASWebAuthenticationSession for OAuth. */
function isInAppBrowser(): boolean {
  if (Capacitor.isNativePlatform()) return false;
  const ua = navigator.userAgent || (navigator as any).vendor || '';
  return /FBAN|FBAV|FB_IAB|Instagram|Line\/|Snapchat|Twitter|TikTok|BytedanceWebview|MicroMessenger|WeChat/i.test(ua);
}

interface AuthOverlayProps {
  onClose: () => void;
  onRegistered?: () => void;
}

const AuthOverlay: React.FC<AuthOverlayProps> = ({ onClose, onRegistered }) => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const inAppBrowser = useMemo(() => isInAppBrowser(), []);

  /** Scroll focused input into view when the virtual keyboard opens on mobile */
  const scrollInputIntoView = (e: React.FocusEvent<HTMLInputElement>) => {
    const el = e.target;
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = window.location.href;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    }
  };

  const getAuthErrorMessage = (err: any): string => {
    const code = err?.code || '';
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
      return t('auth.errors.invalid_credentials');
    }
    if (code === 'auth/invalid-email') {
      return t('auth.errors.invalid_email');
    }
    if (code === 'auth/too-many-requests') {
      return t('auth.errors.too_many_requests');
    }
    if (code === 'auth/operation-not-allowed') {
      return t('auth.errors.operation_not_allowed');
    }
    if (code === 'auth/email-already-in-use') {
      return t('auth.errors.email_already_in_use');
    }
    if (code === 'auth/weak-password') {
      return t('auth.errors.weak_password');
    }
    return `${err?.message || t('auth.errors.default')}${code ? ` (${code})` : ''}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await loginUser(email, password);
      } else {
        const userCred = await registerUser(email, password);
        if (userCred.user) {
          await updateUserProfile(userCred.user, { displayName: name });
          await updateUserSettings(userCred.user.uid, {
            subscriptionStatus: 'free',
            offroadRoadFilters: {
              track: false,
              unclassified: false,
              service: false,
              residential: false
            }
          });
        }
        onRegistered?.();
      }
      onClose();
    } catch (err: any) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await handleGoogleLogin();
      onClose();
    } catch (err: any) {
      setError(getAuthErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const onAppleLogin = async () => {
    setError('');
    setAppleLoading(true);
    try {
      await handleAppleLogin();
      onClose();
    } catch (err: any) {
      console.error('Apple Sign-In error:', JSON.stringify({ message: err?.message, code: err?.code }, null, 2));
      // Silently ignore user cancellation of the Apple sign-in dialog
      const msg = (err?.message || '').toLowerCase();
      const code = (err?.code || '').toLowerCase();
      if (msg.includes('cancel') || msg.includes('abort') || code.includes('cancel') ||
          code === '1001' || msg.includes('1001')) {
        // User cancelled — do nothing
      } else {
        setError(getAuthErrorMessage(err));
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleResetPassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail || resetLoading) return;
    setError('');
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(cleanEmail);
      setResetSent(true);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(getAuthErrorMessage(err) || t('auth.reset_password_error'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-end md:items-center justify-center animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-stone-950/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--bg-surface)] md:border border-[var(--border-secondary)] rounded-t-modal md:rounded-modal shadow-3xl overflow-hidden animate-in zoom-in-95 fade-in duration-300 max-h-full flex flex-col" style={{ paddingTop: 'calc(var(--safe-area-top, 0px) + 1.5rem)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="p-5 sm:p-8 max-h-[90dvh] overflow-y-auto custom-scrollbar" style={{ overscrollBehavior: 'contain' }}>
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tighter">
              {isLogin ? t('auth.welcome_back') : t('auth.create_account')}
            </h2>
            <button onClick={onClose} className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
              <X size={24} />
            </button>
          </div>

          {showResetPassword ? (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => { setShowResetPassword(false); setError(''); setResetSent(false); }}
                className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-2xs font-black uppercase tracking-widest transition-colors"
              >
                <ArrowLeft size={14} />
                {t('auth.back_to_login')}
              </button>

              <h3 className="text-sm font-black text-[var(--text-primary)]">{t('auth.reset_password_title')}</h3>

              {resetSent ? (
                <div className="space-y-4 animate-in fade-in">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl space-y-2">
                    <div className="flex items-center gap-2 text-emerald-400 text-sm font-black">
                      <Check size={18} />
                      {t('auth.reset_password_sent')}
                    </div>
                    <p className="text-emerald-300/90 text-xs leading-relaxed">
                      {t('auth.reset_password_sent_desc', 'Hvis adressen er registrert med passord, har vi sendt en tilbakestillingslenke til e-posten din.')}
                    </p>
                  </div>

                  <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-2xl space-y-1.5">
                    <p className="text-amber-400 text-xs font-bold flex items-center gap-1.5">
                      <AlertCircle size={14} />
                      {t('auth.reset_password_spam_title', 'Finner du ikke e-posten?')}
                    </p>
                    <p className="text-[var(--text-secondary)] text-tiny leading-relaxed">
                      {t('auth.reset_password_spam_notice', 'Husk å sjekke søppelpost/spam-mappen din. E-posten sendes fra noreply@fricamp-2810e.firebaseapp.com.')}
                    </p>
                  </div>

                  <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-4 rounded-2xl space-y-1.5">
                    <p className="text-[var(--text-primary)] text-xs font-bold">
                      {t('auth.reset_password_oauth_title', 'Bruker du Google eller Apple?')}
                    </p>
                    <p className="text-[var(--text-secondary)] text-tiny leading-relaxed">
                      {t('auth.reset_password_oauth_notice', 'Registrerte du deg opprinnelig med Google eller Apple? Da har du ikke eget passord. Gå tilbake og logg inn direkte med Google eller Apple.')}
                    </p>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => { setResetSent(false); setError(''); }}
                      className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] hover:bg-[var(--bg-surface-hover)] text-[var(--text-primary)] py-3 rounded-xl font-bold text-xs transition-colors text-center"
                    >
                      {t('auth.reset_password_try_another', 'Prøv en annen e-post')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowResetPassword(false); setError(''); setResetSent(false); }}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all text-center"
                    >
                      {t('auth.back_to_login')}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <p className="text-[var(--text-secondary)] text-xs leading-relaxed">
                    {t('auth.reset_password_subtitle', 'Skriv inn e-postadressen din, så sender vi en lenke for å tilbakestille passordet.')}
                  </p>

                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
                    <input
                      type="email"
                      id="auth-reset-email"
                      aria-label={t('auth.email')}
                      autoComplete="email"
                      placeholder={t('auth.email')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={scrollInputIntoView}
                      disabled={resetLoading}
                      required
                      className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-500/40 text-[var(--text-primary)] disabled:opacity-60"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-2xl flex items-center gap-3 text-red-400 text-xs font-bold animate-in fade-in">
                      <AlertCircle size={16} className="shrink-0" /> {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!email || resetLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"
                  >
                    {resetLoading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Mail size={18} />
                    )}
                    {resetLoading ? t('common.loading', 'Laster...') : t('auth.reset_password_btn')}
                  </button>
                </form>
              )}
            </div>
          ) : (<>

          {inAppBrowser && (
            <div className="mb-4 bg-amber-500/10 border border-amber-500/30 p-4 rounded-2xl animate-in fade-in">
              <p className="text-amber-300 text-xs font-bold mb-2">
                {t('auth.inapp_browser_title')}
              </p>
              <p className="text-[var(--text-secondary)] text-tiny leading-relaxed mb-3">
                {t('auth.inapp_browser_desc')}
              </p>
              <button
                onClick={handleCopyLink}
                className="w-full bg-amber-500/20 border border-amber-500/30 text-amber-300 py-3 rounded-xl font-black text-2xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-amber-500/30 transition-all active:scale-95"
              >
                {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                {linkCopied ? t('auth.link_copied') : t('auth.copy_link')}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
                <input
                  type="text"
                  id="auth-name"
                  aria-label={t('auth.full_name')}
                  autoComplete="name"
                  placeholder={t('auth.full_name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onFocus={scrollInputIntoView}
                  className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-500/40 text-[var(--text-primary)]"
                  required
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
              <input
                type="email"
                id="auth-email"
                aria-label={t('auth.email')}
                autoComplete="email"
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={scrollInputIntoView}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-500/40 text-[var(--text-primary)]"
                required
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
              <input
                type="password"
                id="auth-password"
                aria-label={t('auth.password')}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={scrollInputIntoView}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-500/40 text-[var(--text-primary)]"
                required
              />
            </div>

            {isLogin && (
              <div className="flex justify-end -mt-2 mb-1">
                <button
                  type="button"
                  onClick={() => { setShowResetPassword(true); setError(''); setResetSent(false); }}
                  className="text-3xs font-black text-[var(--text-muted)] hover:text-emerald-400 uppercase tracking-widest transition-colors"
                >
                  {t('auth.forgot_password')}
                </button>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-2xl flex items-center gap-3 text-red-400 text-xs font-bold animate-in shake">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || googleLoading || appleLoading}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <>
                  {isLogin ? <LogIn size={18} /> : <UserPlus size={18} />}
                  {isLogin ? t('auth.login_btn') : t('auth.register_btn')}
                </>
              )}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[var(--bg-input)]" />
            <span className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('auth.or_continue_with')}</span>
            <div className="flex-1 h-px bg-[var(--bg-input)]" />
          </div>

          <button
            onClick={onAppleLogin}
            disabled={loading || googleLoading || appleLoading || inAppBrowser}
            className="w-full bg-white text-black py-4 rounded-2xl font-black text-2xs uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-stone-200 transition-all active:scale-95 disabled:opacity-50"
          >
            {appleLoading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {t('auth.apple_login')}
              </>
            )}
          </button>

          <button
            onClick={onGoogleLogin}
            disabled={loading || googleLoading || appleLoading || inAppBrowser}
            className="w-full mt-3 bg-[var(--bg-input)] border border-[var(--border-secondary)] text-[var(--text-secondary)] py-4 rounded-2xl font-black text-2xs uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-[var(--bg-input)] transition-all active:scale-95 disabled:opacity-50"
          >
            {googleLoading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                {t('auth.google_login')}
              </>
            )}
          </button>

          <div className="mt-8 pt-6 border-t border-[var(--border-secondary)] text-center">
            <p className="text-[var(--text-muted)] text-2xs font-bold mb-4">
              {isLogin ? t('auth.no_account') : t('auth.already_have_account')}
            </p>
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-emerald-400 hover:text-emerald-300 text-2xs font-black uppercase tracking-widest transition-colors"
            >
              {isLogin ? t('auth.create_one_now') : t('auth.go_to_login')}
            </button>
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
};

export default AuthOverlay;
